import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";

/**
 * Self-serve password reset — step 1 of 2.
 *
 * User enters their email. We POST to /auth/request-password-reset and
 * ALWAYS show the same success message, regardless of whether the email
 * matched a real account. This matches the backend's email-enumeration
 * defense: the UI must not differentiate either.
 *
 * Step 2 is `ResetPassword.jsx`, reached via the link in the email that
 * the backend (well, MockEmailService for now) sends after this succeeds.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Fire-and-forget. The backend returns a generic 200 for both
      // registered and unknown emails, so there's nothing useful to
      // branch on here.
      await api.post("/auth/request-password-reset", { email });
    } catch (_) {
      // Rate-limit (429) or other transient errors are silently
      // absorbed into the same generic success UI. The user tries
      // again if nothing arrives.
    } finally {
      setSubmitted(true);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Reset Password</h1>
          <p className="text-gray-500 mt-2">Enter your email to receive a reset link</p>
        </div>

        {submitted ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
              If that email is registered, we've sent reset instructions to it.
              The link expires in 1 hour.
            </div>
            <p className="text-sm text-gray-500">
              Didn't receive anything? Check your spam folder, then try again.
            </p>
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
            <div className="text-center mt-4">
              <Link
                to="/login"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
